import React, { useEffect, useState } from 'react';
import { Form, Schema, Stack, Col, Grid, Row, IconButton, Divider, FormInstance, SelectPicker } from 'rsuite';
import { useNavigate } from 'react-router-dom';
import { BaseErrorModel, BaseResponseModel, PageState } from '@/models/core.model';
import { ObjectStorageFileCategory } from '@/models/storage.model';
import PageContent from '@/components/PageContent';
import PageNextIcon from '@rsuite/icons/PageNext';
import PagePreviousIcon from '@rsuite/icons/PagePrevious';
import { AxiosError } from 'axios';
import ImageUploader from '@/components/ImageUploader';
import { MdFlag } from 'react-icons/md';
import { AdvanceSharePack, AdvanceSharePackStatusEnum, AdvanceSharePackStatusList, UpsertAdvanceSharePack } from '@/models/advance-share-pack.model';
import { upsertAdvanceSharePack } from '@/services/advance-share-pack.service';
import ErrorMessage from '@/components/ErrorMessage';

const schema = Schema.Model<UpsertAdvanceSharePack>({
  id: Schema.Types.NumberType(),
  title: Schema.Types.StringType().isRequired('This field is required.'),
  coverUrl: Schema.Types.StringType(),
  status: Schema.Types.NumberType().isRequired('This field is required.'),
});

const emptyModel: UpsertAdvanceSharePack = {
  id: undefined,
  title: undefined,
  coverUrl: undefined,
  status: AdvanceSharePackStatusEnum.Draft,
};

interface AdvanceSharePackFormProps {
  formObject?: AdvanceSharePack;
  pageState: PageState;
};

const AdvanceSharePackForm: React.FC<AdvanceSharePackFormProps> = ({ formObject, pageState }) => {
  const [formValue, setFormValue] = useState<UpsertAdvanceSharePack>(emptyModel);
  const [formError, setFormError] = useState<Record<string, any>>({});
  const [errorModel, setErrorModel] = useState<BaseErrorModel | undefined>(undefined);
  const [loading, setLoading] = useState(false);

  const nav = useNavigate();
  const formRef = React.useRef<FormInstance<Record<string, any>>>(null);

  useEffect(() => {
    if (pageState == PageState.New) {
      setFormValue({ ...emptyModel });
      return;
    }

    if (formObject) {
      const castedModel: UpsertAdvanceSharePack = {
        ...formObject,
      };

      setFormValue(castedModel);
    } else {
      setFormValue(emptyModel);
    }
  }, [formObject]);

  const handleSubmit = async () => {
    if (formRef.current && !formRef.current.check()) {
      console.log("errors:", formError);
      return;
    }
    setErrorModel(undefined);
    setLoading(true);
    try {
      const result: BaseResponseModel<any> = await upsertAdvanceSharePack(formValue);
      if (result.success) {
        nav('/advance-shares/packs');
      }
    } catch (err) {
      setErrorModel((err as AxiosError<BaseErrorModel>).response?.data);
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (value: Record<string, any>) => {
    setFormValue(prevFormValue => ({
      ...prevFormValue,
      ...value
    }));
  };

  return (
    <PageContent>
      <Form name='advance-share-pack-form' fluid
        ref={formRef}
        onChange={handleFormChange}
        onCheck={setFormError}
        formValue={formValue}
        model={schema}
        disabled={loading}
      >
        <Grid w='100%' fluid >
          <Row justifyContent="center">
            <Col span={{ xs: 24, xl: 12 }} >
              <Form.Group>
                <Form.Label>Cover Image</Form.Label>
                <Form.Control
                  accepter={ImageUploader}
                  name="coverUrl"
                  defaultIcon={MdFlag}
                  height="250px"
                  url={formValue.coverUrl}
                  objectStorageCategory={ObjectStorageFileCategory.AdvanceShare}
                  onChange={url => setFormValue(pre => ({ ...pre, coverUrl: url }))}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row justifyContent="center">
            <Col span={{ xs: 24, xl: 12 }} >
              <Form.Group>
                <Form.Label>Title</Form.Label>
                <Form.Control name="title" value={formValue.title ?? ''} />
              </Form.Group>
            </Col>
          </Row>
          <Row justifyContent="center">
            <Col span={{ xs: 24, xl: 12 }} >
              <Form.Group>
                <Form.Label>Status</Form.Label>
                <Form.Control name="status" accepter={SelectPicker} block searchable={false}
                  data={AdvanceSharePackStatusList}
                />
              </Form.Group>
            </Col>
          </Row>
        </Grid>
      </Form>

      <Divider />
      {errorModel && <ErrorMessage model={errorModel} style={{ marginTop: 20, marginBottom: 20 }}></ErrorMessage>}

      <Stack justifyContent="space-between">
        <IconButton icon={<PagePreviousIcon />} onClick={() => nav(-1)}>
          Back
        </IconButton>
        <IconButton
          icon={<PageNextIcon />}
          placement="right"
          appearance="primary"
          onClick={handleSubmit}
          loading={loading}
        >
          Save
        </IconButton>
      </Stack>
    </PageContent>
  );
};

export default AdvanceSharePackForm;
